import React, { useEffect, useState, useCallback } from "react";
import { PrimerosPasos } from "../components/PrimerosPasos";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { usePlanActual, useDatosAlta } from "../lib/planes";
import BannerAlcance, { esAcotada } from "../components/BannerAlcance";
import NewAppointmentModal from "../components/NewAppointmentModal";
import NewPatientModal from "../components/NewPatientModal";
import { TarjetaConsumo, tarjetaVisible } from "../components/ConsumoSofi";
import { useConsumo } from "../contexts/ConsumoContext";
import { toast } from "sonner";
import {
  Calendar,
  CalendarDays,
  UserPlus,
  MessageCircle,
  Bot,
  Users,
  Clock,
  ArrowRight,
  X,
  PartyPopper,
  Settings,
  Plus,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const DAYS_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
const MONTHS_ES = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function todayLabel() {
  const d = new Date();
  return `${DAYS_ES[d.getDay()]}, ${d.getDate()} de ${MONTHS_ES[d.getMonth()]} de ${d.getFullYear()}`;
}

function dayLabel(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return `${d.getDate()}/${d.getMonth() + 1}`;
}

function KpiCard({ icon: Icon, iconColor, iconBg, value, label, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
      <div className="font-bold text-3xl text-slate-900 tracking-tight">{value ?? "—"}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
      {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [overview, setOverview] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [sofi, setSofi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openAppt, setOpenAppt] = useState(false);
  const [openPat, setOpenPat] = useState(false);
  const [showActivatedBanner, setShowActivatedBanner] = useState(false);
  // TEN.4: ¿el plan incluye a Sofi? UNA fuente para todo el panel (`usePlanActual`).
  const planActual = usePlanActual();
  const sinSofi = planActual.sinSofi;
  const conSofi = planActual.estado !== "cargando" && !sinSofi;
  const conexionWa = useDatosAlta().datos?.conexion_whatsapp_url; // TEN.4: una sola constante
  const nav = useNavigate();
  const { user: authUser, activeClinic, puede} = useAuth();
  // ¿WhatsApp conectado? De la FUENTE (`GET /whatsapp/status`, proxy a agency). `null` = no se
  // sabe (cargando o error): el botón de agendar la conexión sólo sale con `connected === false`.
  const [waConectado, setWaConectado] = useState(null);
  const pedirEstadoWa = Boolean(activeClinic && !activeClinic.is_demo && conSofi && conexionWa);
  useEffect(() => {
    if (!pedirEstadoWa) return undefined;
    let vivo = true;
    setWaConectado(null);
    api.get("/whatsapp/status")
      .then(({ data }) => { if (vivo) setWaConectado(data?.connected === true); })
      .catch(() => { if (vivo) setWaConectado(null); });
    return () => { vivo = false; };
  }, [pedirEstadoWa, activeClinic?.id]);
  const firstName = (authUser?.name || "").split(" ")[0] || "equipo";

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("activated") === "true") {
      setShowActivatedBanner(true);
      const t = setTimeout(() => setShowActivatedBanner(false), 10000);
      return () => clearTimeout(t);
    }
  }, []);

  // 🔴 SIN `agenda` NO SE PIDEN LAS METRICAS, y esto es lo mas importante de la pantalla.
  //    Los tres endpoints exigen esa capacidad, asi que un colaborador sin ella recibia
  //    403 en los tres, el `Promise.all` rechazaba entero y el Dashboard -- que es la
  //    ruta INDICE, la primera que ve al entrar -- se quedaba con un toast rojo generico
  //    ("No se pudieron cargar las metricas") y las tarjetas en cero. Indistinguible de
  //    una averia, y con ceros que ademas son falsos: la clinica si tiene citas, sólo que
  //    a el no le corresponden. Medido en produccion antes de tocar: 403/403/403.
  //    Esconder no protege nada (el servidor ya rechaza); lo que evita es que el panel se
  //    presente roto a quien acaba de entrar.
  const verMetricas = puede("agenda");
  // LIM.5: ¿hay consumo del mes que contar? (estado y cifras, del servidor).
  // LIM.11 I8b: o la tarjeta de la prueba gratis.
  const verConsumo = tarjetaVisible(useConsumo().consumo);
  // Del bloque que manda el backend, no de una deducción local: `alcance.total` viaja
  // ya RESUELTO justamente para que ninguna pantalla lo infiera de una lista vacía.
  const acotado = overview?.alcance && overview.alcance.total === false;

  const loadData = useCallback(() => {
    if (!verMetricas) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      api.get("/metrics/overview?days=30"),
      api.get("/metrics/appointments-by-day?days=30"),
      api.get("/metrics/sofi-activity?days=30"),
    ])
      .then(([ov, byDay, sofiAct]) => {
        setOverview(ov.data);
        setChartData((byDay.data || []).map((d) => ({ ...d, label: dayLabel(d.date) })));
        setSofi(sofiAct.data);
      })
      .catch(() => toast.error("No se pudieron cargar las métricas"))
      .finally(() => setLoading(false));
  }, [verMetricas]);

  useEffect(() => {
    loadData();
  }, [loadData]);


  const onApptCreated = () => { toast.success("Cita creada"); loadData(); };
  const onPatientCreated = () => { toast.success("Paciente registrado"); loadData(); };

  return (
    <div className="p-6 lg:p-8 space-y-6 max-w-7xl mx-auto w-full">
      <PrimerosPasos ocultarSiCompleto />

      {/* Banner activación post-pago */}
      {showActivatedBanner && (
        <div className="flex items-start gap-3 rounded-2xl bg-emerald-50 border border-emerald-200 px-5 py-4">
          <PartyPopper className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            {/* TEN.4: decia "¡Tu clinica fue activada! Revisa tu correo — te enviamos el link del
                formulario de configuracion". Ese correo ya no se manda en clinicas, y "activada" se
                afirmaba antes de que el webhook confirmara nada. */}
            <p className="font-semibold text-emerald-900 text-sm">Recibimos tu solicitud de pago</p>
            <p className="text-emerald-700 text-xs mt-0.5">
              Tu plan se actualiza en cuanto el pago se confirme, y te avisamos por correo.
            </p>
          </div>
          <button onClick={() => setShowActivatedBanner(false)} className="shrink-0 text-emerald-500 hover:text-emerald-700 p-1 rounded-lg">
            <X size={16} />
          </button>
        </div>
      )}

      {/*
        WhatsApp. Sólo para una clínica SIN WhatsApp conectado, medido con `GET /whatsapp/status`
        (25 Oct 2026, pedido de Jesús: con el WhatsApp ya conectado el botón seguía saliendo). Si el
        estado no se pudo leer, no se ofrece: «no sé» no es «no está conectada».
      */}
      {pedirEstadoWa && waConectado === false && (
        <div className="bg-white border border-blue-100 rounded-2xl shadow-sm p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
            <MessageCircle className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-900 text-sm">WhatsApp de tu clínica</div>
            <p className="text-xs text-slate-500 mt-0.5">La conexión del WhatsApp de tu clínica la hacemos contigo.</p>
          </div>
          <a
            href={conexionWa}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 bg-blue-600 text-white text-xs font-medium px-3 py-2 rounded-lg hover:bg-blue-700"
          >
            Agendar la conexión
          </a>
        </div>
      )}

      {/* SECCIÓN 1 — Saludo + fecha */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Buenos días, {firstName}</h1>
        <p className="text-sm text-slate-500 mt-0.5">{todayLabel()}</p>

        {/* Próxima cita destacada */}
        {!loading && overview?.next_appointment && (
          <div className="mt-3 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5">
            <Clock className="w-4 h-4 text-amber-600 shrink-0" />
            <span className="text-sm text-amber-900 font-medium">
              Próxima cita: {overview.next_appointment.time?.slice(0, 5)} — {overview.next_appointment.patient_name}
              {overview.next_appointment.doctor_name && ` con ${overview.next_appointment.doctor_name}`}
            </span>
          </div>
        )}
      </div>

      {/* SECCIÓN 1-bis — el alcance con el que está calculado TODO lo de abajo.
          Se declara SIEMPRE, también cuando es total: un aviso que sólo aparece a veces
          obliga a deducir el caso por omisión, que es como se lee al revés el
          "sin filas = toda la clínica" de COL.8.
          `mezcla` porque aquí conviven cifras acotadas (citas) con cifras de la clínica
          entera (pacientes, conversaciones) -- y eso no se puede resolver con una sola
          etiqueta sin mentir en una de las dos direcciones. */}
      {verMetricas && !loading && overview?.alcance && (
        <BannerAlcance alcance={overview.alcance} mezcla />
      )}

      {/* Sin `agenda` no hay métricas que pedir. Se dice qué pasa y qué hacer, en vez de
          pintar ceros y un toast de error -- un cero aquí afirmaría que la clínica no
          tuvo citas, y es falso: no le corresponden. */}
      {!verMetricas && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="dash-sin-agenda">
          <p className="font-semibold text-slate-900">Tu acceso no incluye la agenda</p>
          <p className="text-sm text-slate-600 mt-1">
            Por eso esta pantalla no muestra citas ni métricas: no son cifras que te
            correspondan, y enseñarlas en cero diría que la clínica no tuvo ninguna.
            En el menú de la izquierda tienes lo que sí puedes usar.
          </p>
        </div>
      )}

      {/* SECCIÓN 2 — KPIs del día */}
      {verMetricas && (loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={Calendar}
            iconColor="text-blue-600"
            iconBg="bg-blue-50"
            value={overview?.appointments_today}
            label={esAcotada(overview?.alcance, "appointments_today") ? "Tus citas hoy" : "Citas hoy"}
            sub={esAcotada(overview?.alcance, "appointments_today") ? "sólo tus médicos" : undefined}
          />
          <KpiCard
            icon={CalendarDays}
            iconColor="text-blue-600"
            iconBg="bg-blue-50"
            value={overview?.appointments_this_month}
            label={esAcotada(overview?.alcance, "appointments_this_month") ? "Tus citas este mes" : "Citas este mes"}
            sub={esAcotada(overview?.alcance, "appointments_this_month") ? "sólo tus médicos" : undefined}
          />
          <KpiCard
            icon={UserPlus}
            iconColor="text-emerald-600"
            iconBg="bg-emerald-50"
            value={overview?.patients_new_this_month}
            label="Pacientes nuevos"
            sub={`${overview?.patients_total ?? 0} total${acotado ? " — de toda la clínica" : ""}`}
          />
          {/* El `sub` de esta tarjeta decia "N citas agendadas" con N = sofi_bookings_this_month,
              que es un LITERAL 0 en el backend desde S6 (7 Ago 2026). Retirado el 12 Sep 2026;
              ver Metricas.js y el pendiente de columna de origen en CLAUDE.md. */}
          {!sinSofi && (
          <KpiCard
            icon={MessageCircle}
            iconColor="text-violet-600"
            iconBg="bg-violet-50"
            value={overview?.sofi_conversations_this_month}
            label="Conversaciones Sofi"
            sub={acotado ? "de toda la clínica" : undefined}
          />
          )}
        </div>
      ))}

      {/* SECCIÓN 3 — Gráfica citas por día. Bajo el mismo guardia: sin `agenda` no hay
          filas que pintar y el estado vacío diría "Sin datos este período", que afirma
          algo falso sobre la clínica. */}
      {verMetricas && (
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-semibold text-slate-900">Citas — últimos 30 días</div>
            <div className="text-xs text-slate-400">Actividad del sistema médico</div>
          </div>
          {puede("agenda") && (
            <Link to="/app/metricas" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
              Ver detalle <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
        {chartData.length === 0 ? (
          <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">Sin datos este período</div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#94A3B8" }}
                interval={4}
                axisLine={false}
                tickLine={false}
              />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip
                cursor={{ fill: "#F1F5F9" }}
                contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }}
                formatter={(v) => [v, "Citas"]}
              />
              <Bar dataKey="count" fill="#2563EB" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      )}

      {/* TEN.4 -- LA invitación a contratar a Sofi: UN solo sitio en todo el panel (decisión de
          Jesús), y sólo en un plan que no la incluye. Lleva a la pantalla de planes.
          ⚠️ COPY PENDIENTE DE CONFIRMAR. */}
      {sinSofi && puede("configuracion") && (
        <div className="rounded-2xl border border-[#DDD6FE] p-5 shadow-sm flex flex-wrap items-center justify-between gap-3" style={{ background: "#F5F3FF" }} data-testid="invitacion-sofi">
          <div className="flex items-center gap-3">
            <Bot className="w-5 h-5 text-violet-600" />
            <div>
              <div className="font-semibold text-slate-900 text-sm">Agrega a Sofi a tu clínica</div>
              <div className="text-xs text-slate-600">Sofi atiende el WhatsApp de tu clínica y agenda citas.</div>
            </div>
          </div>
          <Link to="/app/onboarding" className="text-xs font-medium text-violet-700 hover:underline flex items-center gap-1">
            Ver planes con Sofi <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {/* SECCIÓN 4 — Actividad de Sofi.
          LIM.5: el consumo del mes va DENTRO de esta sección, bajo su mismo título, en vez de en
          una tarjeta aparte. La sección se pinta si hay actividad (exige `agenda`) O si hay
          consumo que contar (lo ve toda sesión: el aviso de pausa no puede depender de `agenda`).
          Las cifras de actividad siguen saliendo sólo con `sofi`. */}
      {!sinSofi && ((!loading && sofi) || verConsumo) && (
        <div className="rounded-2xl border border-[#DDD6FE] p-5 shadow-sm" style={{ background: "#F5F3FF" }} data-testid="seccion-sofi">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Bot className="w-5 h-5 text-violet-600" />
              <span className="font-semibold text-slate-900">Sofi este mes</span>
            </div>
            {puede("configuracion") && (
              <Link to="/app/asistente" className="text-xs text-violet-600 hover:underline flex items-center gap-1">
                Ver configuración <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
          <TarjetaConsumo />
          {!loading && sofi && (<>
          <div className="flex gap-6 mb-4">
            <div>
              <span className="text-2xl font-bold text-slate-900">{sofi.total_conversations ?? "—"}</span>
              <div className="text-xs text-slate-500">conversaciones</div>
            </div>
            {/* "citas agendadas" retirado: `total_bookings` es un literal 0 desde S6. */}
          </div>
          {sofi.conversations_by_day?.length > 0 && (
            <ResponsiveContainer width="100%" height={80}>
              <BarChart data={sofi.conversations_by_day.map((d) => ({ ...d, label: dayLabel(d.date) }))} margin={{ top: 0, right: 0, bottom: 0, left: -30 }}>
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#A78BFA" }} interval={6} axisLine={false} tickLine={false} />
                <YAxis hide allowDecimals={false} />
                <Tooltip
                  cursor={{ fill: "rgba(167,139,250,0.15)" }}
                  contentStyle={{ borderRadius: 8, border: "1px solid #DDD6FE", fontSize: 11 }}
                  formatter={(v) => [v, "Conv."]}
                />
                <Bar dataKey="count" fill="#7C3AED" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
          </>)}
        </div>
      )}

      {/* SECCIÓN 5 — Accesos rápidos */}
      <div>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Accesos rápidos</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {/* 🔴 Cada acceso rápido se oculta con la MISMA regla que su entrada de menú y
              leyendo del mismo sitio (`puede`, de la sesión). Sin esto el panel ofrece
              una puerta que el servidor cierra con 403 -- y un botón que el propio panel
              pone enfrente y falla no se lee como "no tienes permiso", se lee como una
              avería (DT.71). Medido: Carlos no tiene `configuracion` y el menú ya lo
              reflejaba, pero aquí seguía viendo "Configurar Sofi".
              ⚠️ Los dos primeros no navegan: ABREN UN FORMULARIO. Dejarlos es peor que un
              enlace roto -- se teclea una cita entera y el 403 llega al guardar, con lo
              escrito perdido. */}
          {puede("agenda") && (
            <QuickAction icon={Plus} label="Nueva cita" onClick={() => setOpenAppt(true)} />
          )}
          {puede("pacientes") && (
            <QuickAction icon={UserPlus} label="Agregar paciente" onClick={() => setOpenPat(true)} />
          )}
          {puede("pacientes") && (
            <QuickAction icon={Users} label="Ver pacientes" onClick={() => nav("/app/pacientes")} />
          )}
          {puede("configuracion") && !sinSofi && (
            <QuickAction icon={Settings} label="Configurar Sofi" onClick={() => nav("/app/asistente")} />
          )}
        </div>
      </div>

      <NewAppointmentModal open={openAppt} onClose={() => setOpenAppt(false)} onCreated={onApptCreated} />
      <NewPatientModal open={openPat} onClose={() => setOpenPat(false)} onCreated={onPatientCreated} />
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="bg-white border border-slate-200 rounded-2xl p-4 text-left hover:bg-slate-50 hover:border-slate-300 transition-colors flex items-center gap-3"
    >
      <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div className="font-medium text-sm text-slate-700">{label}</div>
    </button>
  );
}
